import type { AgentId } from '@quintessence/core';
import { useEffect, useRef } from 'react';
import { NeuralFieldEngine, type FieldOptions, type FieldVariant } from '../neural/field';

export interface FieldSignal {
  id: string;
  agent: AgentId;
}

/**
 * Decorative canvas that fills its positioned parent. Pauses off-screen and in
 * background tabs; draws one still frame under prefers-reduced-motion.
 * New entries in `signals` fire their agent's hub (history on mount does not).
 */
export function NeuralField({
  variant = 'hero',
  signals,
  tint,
  focus,
  className,
}: {
  variant?: FieldVariant;
  signals?: FieldSignal[];
  className?: string;
} & FieldOptions) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const engine = useRef<NeuralFieldEngine | null>(null);
  const seen = useRef<Set<string> | null>(null);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    const field = new NeuralFieldEngine(el, variant, { tint, focus });
    engine.current = field;

    let visible = false;
    const sync = () => (visible && !document.hidden ? field.start() : field.stop());
    const resize = new ResizeObserver(([entry]) => field.resize(entry.contentRect.width, entry.contentRect.height));
    resize.observe(el);
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      sync();
    });
    io.observe(el);
    const onMove = (e: PointerEvent) => field.setPointer(e.clientX, e.clientY);
    const onLeave = () => field.clearPointer();
    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('pointerleave', onLeave);
    document.addEventListener('visibilitychange', sync);

    return () => {
      field.stop();
      resize.disconnect();
      io.disconnect();
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerleave', onLeave);
      document.removeEventListener('visibilitychange', sync);
      engine.current = null;
    };
    // Options are read once per mount; key the component to change them.
  }, [variant]);

  useEffect(() => {
    if (!signals) return;
    if (!seen.current) {
      // Wait for the first non-empty history so it is not replayed as a burst.
      if (!signals.length) return;
      seen.current = new Set(signals.map((s) => s.id));
      return;
    }
    const fresh = signals.filter((s) => !seen.current!.has(s.id));
    for (const s of fresh.reverse()) {
      seen.current.add(s.id);
      engine.current?.fire(s.agent);
    }
  }, [signals]);

  return <canvas ref={canvas} className={`neural-field ${className ?? ''}`} aria-hidden />;
}
